import * as React from 'react'
import { Col, Row } from 'react-bootstrap'
import { CgChevronLeft, CgChevronRight } from 'react-icons/cg'
import { StaticQuery, graphql } from 'gatsby'
import slugify from '@sindresorhus/slugify'
import debounce from 'debounce'
import Layout from '../components/Layout'
import ContentContainer from '../components/content/ContentContainer/ContentContainer'
import ContentCover from '../components/content/ContentCover/ContentCover'
import { Filters } from '../components/filter/Filters/Filters'
import { Spinner } from '../components/filter/Progress/Progress'
import CollectionGrid from '../components/products/CollectionGrid/CollectionGrid'
import { ProductCard } from '../components/products/ProductCard/ProductCard'
import { Seo } from '../components/seo'
import { SearchProvider } from '../context/search-provider'
import { StoreContext } from '../context/store-context'
import CrossIcon from '../icons/cross'
import FilterIcon from '../icons/filter'
import SearchIcon from '../icons/search'
import SortIcon from '../icons/sort'
import { getCurrencySymbol } from '../utils/format-price'
import { getValuesFromQueryString, useProductSearch } from '../utils/hooks'
import {
  activeFilters,
  clearSearch,
  filterButton,
  filterStyle,
  filterTitle,
  filterWrap,
  main,
  modalOpen,
  pagination,
  paginationButton,
  productListItem,
  productList as productListStyle,
  progressStyle,
  results,
  resultsStyle,
  search,
  searchForm,
  searchIcon,
  sortIcon,
  sortSelector,
  visuallyHidden,
} from './search-page.module.css'

export const query = graphql`
  query {
    meta: allShopifyProduct {
      productTypes: distinct(field: { productType: SELECT })
      tags: distinct(field: { tags: SELECT })
      vendors: distinct(field: { vendor: SELECT })
    }
    allContentfulShopCategory: allContentfulShopCategory(
      sort: { order: ASC }
      skip: 1
      limit: 8
      filter: { url: { ne: "hidden" } }
    ) {
      edges {
        node {
          category
          description {
            description
          }
          featuredCategory
          url
          image {
            gatsbyImageData(width: 400, height: 400)
          }
        }
      }
    }
    contentfulHomePage: contentfulHomePageData {
      collectionsGridTitle
      collectionsGridDescription
    }
    products: allShopifyProduct(
      limit: 24
      sort: { title: ASC }
      filter: { collections: { elemMatch: { handle: { ne: "hidden" } } } }
    ) {
      edges {
        node {
          title
          vendor
          productType
          handle
          priceRangeV2 {
            minVariantPrice {
              currencyCode
              amount
            }
            maxVariantPrice {
              currencyCode
              amount
            }
          }
          id
          media {
            ... on ShopifyMediaImage {
              image {
                gatsbyImageData(aspectRatio: 1, width: 200, layout: FIXED)
              }
            }
          }
          variants {
            price
            metafields {
              key
              value
            }
          }
          metafields {
            key
            value
          }
        }
      }
    }
    metafields: allShopifyProduct {
      nodes {
        id
        title
        metafields {
          key
          value
        }
        variants {
          price
          metafields {
            key
            value
          }
        }
      }
    }
  }
`

function SearchPage({
  data: {
    meta: { productTypes, vendors, tags },
    allContentfulShopCategory,
    contentfulHomePage,
    products,
    metafields,
  },
  location,
}) {
  const { hiddenIds } = React.useContext(StoreContext)
  const dataHomePage = contentfulHomePage

  // Future Fragment
  const dataCategory = allContentfulShopCategory.edges

  // Category Section
  // Contentful needs to be fixed
  const categorySection = {
    title: dataHomePage.collectionsGridTitle,
    description:
      dataHomePage.collectionsGridDescription,
    data: dataCategory,
  }

  // These default values come from the page query string
  const queryParams = getValuesFromQueryString(location.search)
  const [filters, setFilters] = React.useState(queryParams)
  const [sortKey, setSortKey] = React.useState(queryParams.sortKey)
  // We clear the hash when searching, we want to make sure the next page will be fetched due the #more hash.
  const shouldLoadNextPage = React.useRef(false)

  // This modal is only used on mobile
  const [showModal, setShowModal] = React.useState(false)

  const {
    data,
    isFetching,
    filterCount,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
  } = useProductSearch(
    filters,
    {
      allProductTypes: productTypes,
      allVendors: vendors,
      allTags: tags,
    },
    sortKey,
    false,
    24, // Products per page
  )

  // If we're using the default filters, use the products from the Gatsby data layer.
  // Otherwise, use the data from search.
  const isDefault = !data
  const productList = (isDefault ? products.edges : data?.products?.edges) ?? []
  const fields = metafields.nodes

  // Scroll up when navigating
  React.useEffect(() => {
    if (!showModal) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
        // eslint-disable-next-line react-hooks/exhaustive-deps
      })
    }
  }, [productList, showModal])

  // Stop page from scrolling when modal is visible
  React.useEffect(() => {
    if (showModal) {
      document.documentElement.style.overflow = 'hidden'
    } else {
      document.documentElement.style.overflow = ''
    }
  }, [showModal])

  // Automatically load the next page if "#more" is in the URL
  React.useEffect(() => {
    if (location.hash === '#more') {
      // save state so we can fetch it when the first page got fetched to retrieve the cursor
      shouldLoadNextPage.current = true
    }

    if (shouldLoadNextPage.current) {
      if (hasNextPage) {
        fetchNextPage()
      }

      shouldLoadNextPage.current = false
    }
  }, [location.hash])

  const currencyCode = getCurrencySymbol(
    products?.[0]?.node?.priceRangeV2?.minVariantPrice?.currencyCode,
  )

  return (
    <Layout>
      <Seo title={'Search'} search={filters.term} />
      <ContentCover>
        <ContentContainer>
          <h1 className={visuallyHidden}>Search Results</h1>
          <div className={main}>
            <div className={search} aria-hidden={modalOpen}>
              <SearchBar defaultTerm={filters.term} setFilters={setFilters} />
              <button
                className={[
                  filterButton,
                  filterCount ? activeFilters : undefined,
                ].join(' ')}
                onClick={() => setShowModal((show) => !show)}
                // This is hidden because the filters are already visible to
                // screenreaders, so the modal isnt needed.
                aria-hidden
              >
                <FilterIcon />
              </button>
              <div className={sortSelector}>
                <label>
                  <span>Sort by:</span>
                  <select
                    value={sortKey}
                    // eslint-disable-next-line
                    onChange={(e) => setSortKey(e.target.value)}
                  >
                    <option value='RELEVANCE'>Relevance</option>
                    <option value='PRICE'>Price</option>
                    <option value='TITLE'>Title</option>
                    <option value='CREATED_AT'>New items</option>
                    <option value='BEST_SELLING'>Trending</option>
                  </select>
                </label>
                <SortIcon className={sortIcon} />
              </div>
            </div>
            <section
              className={[filterStyle, showModal && modalOpen].join(' ')}
            >
              <div className={filterTitle}>
                <h2>Filter</h2>
                <button aria-hidden onClick={() => setShowModal(false)}>
                  <CrossIcon className='cross-icon' />
                </button>
              </div>
              <div className={filterWrap}>
                <Filters
                  setFilters={setFilters}
                  filters={filters}
                  tags={tags}
                  vendors={vendors}
                  productTypes={productTypes}
                  currencyCode={currencyCode}
                />
              </div>
            </section>
            <section
              className={results}
              aria-busy={isFetching}
              aria-hidden={modalOpen}
            >
              {isFetching ? (
                <p className={progressStyle}>
                  <Spinner aria-valuetext='Searching' /> Searching
                  {filters.term ? ` for "${filters.term}"…` : `…`}
                </p>
              ) : (
                <p className={resultsStyle}>
                  Search results{' '}
                  {filters.term && (
                    <>
                      for &quot;<span>{filters.term}</span>&quot;
                    </>
                  )}
                </p>
              )}
              <Row>
                {!isFetching &&
                  productList
                    .filter((product) => hiddenIds.indexOf(product.node.id) < 0)
                    .map(({ node }, index) => {
                      let metafields
                      let variants

                      fields.map((field) => {
                        if (field.title === node.title) {
                          metafields = field.metafields
                          variants = field.variants
                        }
                      })
                      return (
                        <Col
                          xs={12}
                          md={6}
                          lg={3}
                          className={`px-0 ${productListItem}`}
                          key={node.id}
                        >
                          <ProductCard
                            eager={index === 0}
                            product={{
                              title: node.title,
                              priceRangeV2: node.priceRangeV2,
                              slug: `/products/${node.handle}`,
                              // The search API and Gatsby data layer have slightly different images available.
                              media: isDefault ? node.media : [],
                              storefrontImages: !isDefault && node.images,
                              vendor: node.vendor,
                              metafields: metafields,
                              variants: variants,
                            }}
                            search={true}
                          />
                        </Col>
                      )
                    })}
              </Row>
              {hasPreviousPage || hasNextPage ? (
                <Pagination
                  previousPage={fetchPreviousPage}
                  hasPreviousPage={hasPreviousPage}
                  nextPage={fetchNextPage}
                  hasNextPage={hasNextPage}
                />
              ) : undefined}
            </section>
          </div>
        </ContentContainer>
      </ContentCover>
      <CollectionGrid categories={categorySection} button={true} />
    </Layout>
  )
}

function SearchBar({ defaultTerm, setFilters }) {
  const [term, setTerm] = React.useState(defaultTerm)
  const debouncedSetFilters = React.useCallback(
    debounce((value) => {
      setFilters((filters) => ({ ...filters, term: value }))
    }, 200),
    [setFilters],
  )

  return (
    <form onSubmit={(e) => e.preventDefault()} className={searchForm}>
      <SearchIcon aria-hidden className={searchIcon} />
      <input
        type='text'
        value={term}
        onChange={(e) => {
          setTerm(e.target.value)
          debouncedSetFilters(e.target.value)
        }}
        placeholder='Search...'
      />
      {term && (
        <button
          className={clearSearch}
          type='reset'
          onClick={() => {
            setTerm('')
            setFilters((filters) => ({ ...filters, term: '' }))
          }}
          aria-label='Clear search query'
        >
          <CrossIcon className='cross-icon' />
        </button>
      )}
    </form>
  )
}
/**
 * Shopify only supports next & previous navigation
 */
export function Pagination({
  previousPage,
  hasPreviousPage,
  nextPage,
  hasNextPage,
}) {
  return (
    <nav className={pagination}>
      <button
        className={paginationButton}
        disabled={!hasPreviousPage}
        onClick={previousPage}
        aria-label='Previous page'
      >
        <CgChevronLeft />
      </button>
      <button
        className={paginationButton}
        disabled={!hasNextPage}
        onClick={nextPage}
        aria-label='Next page'
      >
        <CgChevronRight />
      </button>
    </nav>
  )
}

export default function SearchPageTemplate(props) {
  return (
    <SearchProvider>
      <SearchPage {...props} />
    </SearchProvider>
  )
}
